import React from 'react';
import {NavLink} from 'react-router-dom';
import {
    Activity,
    BarChart3,
    Bell,
    ChevronLeft,
    ChevronRight,
    Globe,
    HelpCircle,
    LayoutDashboard,
    LogOut,
    Settings,
    Users,
    Zap
} from 'lucide-react';
import {useDispatch, useSelector} from 'react-redux';
import {toggleSidebarCollapsed} from '@/store/features/uiSlice';

const Sidebar = () => {
    const dispatch = useDispatch();
    const { sidebarCollapsed } = useSelector((state) => state.ui);

    const mainNavItems = [
        {
            id: 'dashboard',
            path: '/',
            label: 'Dashboard',
            icon: LayoutDashboard,
            badge: null
        },
        {
            id: 'sites',
            path: '/sites',
            label: 'Sites',
            icon: Globe,
            badge: 5
        },
        {
            id: 'visitors',
            path: '/visitors',
            label: 'Visiteurs',
            icon: Users,
            badge: '12'
        },
        {
            id: 'analytics',
            path: '/analytics',
            label: 'Analytics',
            icon: Activity,
            badge: null
        },
    ];

    const secondaryNavItems = [
        {
            id: 'activity',
            path: '/activity',
            label: 'Activité',
            icon: BarChart3
        },
        {
            id: 'notifications',
            path: '/notifications',
            label: 'Notifications',
            icon: Bell,
            badge: 3
        },
        {
            id: 'help',
            path: '/help',
            label: 'Aide',
            icon: HelpCircle
        },
    ];

    const settingsNavItems = [
        {
            id: 'settings',
            path: '/settings',
            label: 'Paramètres',
            icon: Settings
        },
    ];

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/login';
    };

    return (
        <div
            className={`
                h-screen flex flex-col
                transition-all duration-300 ease-in-out
                bg-white
                border-r border-gray-200
                shadow-sm
                ${sidebarCollapsed ? 'w-20' : 'w-64'}
                relative group
            `}
        >
            {/* Toggle Button - Toujours visible */}
            <button
                onClick={() => dispatch(toggleSidebarCollapsed())}
                className={`
                    absolute -right-3 top-8 z-20
                    p-2 rounded-full
                    bg-white shadow-lg border border-gray-300
                    hover:bg-gray-50 hover:shadow-xl
                    transition-all duration-200
                    flex items-center justify-center
                    hover:scale-110
                    ${sidebarCollapsed ? 'rotate-180' : ''}
                `}
                title={sidebarCollapsed ? "Agrandir la sidebar" : "Réduire la sidebar"}
            >
                {sidebarCollapsed ? (
                    <ChevronRight size={18} className="text-gray-700" />
                ) : (
                    <ChevronLeft size={18} className="text-gray-700" />
                )}
            </button>

            {/* Brand/Logo - Toujours visible */}
            <div className="p-6">
                <div className="flex items-center space-x-3">
                    <div className="relative">
                        <div className="
                            w-10 h-10 rounded-xl bg-gradient-to-br
                            from-blue-500 to-blue-600 flex items-center
                            justify-center shadow-md
                        ">
                            <Globe className="h-5 w-5 text-white" />
                        </div>
                        <div className="
                            absolute -bottom-1 -right-1 w-4 h-4
                            bg-gradient-to-br from-green-400 to-green-500
                            rounded-full border-2 border-white flex items-center
                            justify-center
                        ">
                            <Zap className="h-2 w-2 text-white" />
                        </div>
                    </div>

                    {/* Texte qui apparaît au hover quand réduit */}
                    {!sidebarCollapsed ? (
                        <div className="flex-1 min-w-0">
                            <h1 className="
                                text-xl font-bold text-gray-900
                                truncate
                            ">
                                SiteTracker
                            </h1>
                            <p className="text-gray-500 text-xs mt-0.5 truncate">
                                Dashboard Pro
                            </p>
                        </div>
                    ) : (
                        // Tooltip au hover quand réduit
                        <div className="
                            hidden group-hover:block absolute left-full ml-2
                            bg-gray-900 text-white px-3 py-2 rounded-lg
                            shadow-xl z-50 whitespace-nowrap
                        ">
                            <p className="font-semibold">SiteTracker</p>
                            <p className="text-xs text-gray-300">Dashboard Pro</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <div className="flex-1 px-4 space-y-8 overflow-y-auto">
                {/* Main Navigation */}
                <div>
                    {!sidebarCollapsed && (
                        <div className="px-4 mb-3">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Navigation
                            </p>
                        </div>
                    )}
                    <nav className="space-y-1">
                        {mainNavItems.map((item) => (
                            <NavLink
                                key={item.id}
                                to={item.path}
                                className={({ isActive }) => `
                                    flex items-center relative group/item
                                    rounded-xl transition-all duration-200
                                    ${sidebarCollapsed ? 'justify-center py-3' : 'px-4 py-3 space-x-3'}
                                    ${isActive
                                    ? 'bg-blue-50 border border-blue-100'
                                    : 'hover:bg-gray-50'
                                }
                                `}
                                title={sidebarCollapsed ? item.label : ''}
                            >
                                {({ isActive }) => (
                                    <>
                                        <div className="relative">
                                            <item.icon className={`
                                                h-5 w-5 transition-colors
                                                ${isActive ? 'text-blue-600' : 'text-gray-500'}
                                                group-hover/item:text-blue-500
                                            `} />

                                            {item.badge && (
                                                <span className={`
                                                    absolute -top-2 -right-2
                                                    px-1.5 py-0.5 min-w-[20px]
                                                    text-[10px] font-bold rounded-full
                                                    bg-gradient-to-r from-red-500 to-pink-500
                                                    text-white flex items-center justify-center
                                                    ${sidebarCollapsed ? 'scale-75' : ''}
                                                    border border-white
                                                `}>
                                                    {item.badge}
                                                </span>
                                            )}
                                        </div>

                                        {/* Texte ou tooltip */}
                                        {!sidebarCollapsed ? (
                                            <>
                                                <span className={`
                                                    text-sm font-medium transition-colors
                                                    ${isActive ? 'text-blue-700' : 'text-gray-700'}
                                                    group-hover/item:text-gray-900
                                                `}>
                                                    {item.label}
                                                </span>
                                                {isActive && (
                                                    <div className="
                                                        absolute right-4 w-2 h-2
                                                        bg-gradient-to-r from-blue-500 to-blue-600
                                                        rounded-full animate-pulse
                                                    " />
                                                )}
                                            </>
                                        ) : (
                                            // Tooltip au hover pour version réduite
                                            <div className="
                                                hidden group-hover/item:block absolute left-full ml-2
                                                bg-gray-900 text-white px-3 py-2 rounded-lg
                                                shadow-xl z-50 whitespace-nowrap
                                            ">
                                                {item.label}
                                                {item.badge && (
                                                    <span className="
                                                        ml-2 px-1.5 py-0.5 text-xs
                                                        bg-red-500 text-white rounded-full
                                                    ">
                                                        {item.badge}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                </div>

                {/* Secondary Navigation */}
                <div>
                    {!sidebarCollapsed && (
                        <div className="px-4 mb-3">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Outils
                            </p>
                        </div>
                    )}
                    <nav className="space-y-1">
                        {secondaryNavItems.map((item) => (
                            <NavLink
                                key={item.id}
                                to={item.path}
                                className={({ isActive }) => `
                                    flex items-center relative group/item
                                    rounded-xl transition-all duration-200
                                    ${sidebarCollapsed ? 'justify-center py-3' : 'px-4 py-3 space-x-3'}
                                    ${isActive
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-50'
                                }
                                `}
                                title={sidebarCollapsed ? item.label : ''}
                            >
                                {({ isActive }) => (
                                    <>
                                        <item.icon className="
                                            h-5 w-5 text-gray-500
                                            group-hover/item:text-gray-700 transition-colors
                                        " />

                                        {!sidebarCollapsed ? (
                                            <>
                                                <span className="
                                                    text-sm font-medium text-gray-700
                                                    group-hover/item:text-gray-900 transition-colors
                                                ">
                                                    {item.label}
                                                </span>
                                                {item.badge && (
                                                    <span className="
                                                        ml-auto px-2 py-0.5 text-xs
                                                        font-medium rounded-full
                                                        bg-gradient-to-r from-orange-500 to-yellow-500
                                                        text-white
                                                    ">
                                                        {item.badge}
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            // Tooltip au hover pour version réduite
                                            <div className="
                                                hidden group-hover/item:block absolute left-full ml-2
                                                bg-gray-900 text-white px-3 py-2 rounded-lg
                                                shadow-xl z-50 whitespace-nowrap
                                            ">
                                                {item.label}
                                                {item.badge && (
                                                    <span className="
                                                        ml-2 px-1.5 py-0.5 text-xs
                                                        bg-orange-500 text-white rounded-full
                                                    ">
                                                        {item.badge}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Bottom Section - Toujours visible */}
            <div className="p-4 border-t border-gray-200">
                <nav className="space-y-1">
                    {settingsNavItems.map((item) => (
                        <NavLink
                            key={item.id}
                            to={item.path}
                            className={({ isActive }) => `
                                flex items-center group/item
                                rounded-xl transition-all duration-200
                                ${sidebarCollapsed ? 'justify-center py-3' : 'px-4 py-3 space-x-3'}
                                ${isActive
                                ? 'bg-gray-100'
                                : 'hover:bg-gray-50'
                            }
                            `}
                            title={sidebarCollapsed ? item.label : ''}
                        >
                            {({ isActive }) => (
                                <>
                                    <Settings className="
                                        h-5 w-5 text-gray-500
                                        group-hover/item:text-gray-700 transition-colors
                                    " />

                                    {!sidebarCollapsed ? (
                                        <span className="
                                            text-sm font-medium text-gray-700
                                            group-hover/item:text-gray-900 transition-colors
                                        ">
                                            {item.label}
                                        </span>
                                    ) : (
                                        <div className="
                                            hidden group-hover/item:block absolute left-full ml-2
                                            bg-gray-900 text-white px-3 py-2 rounded-lg
                                            shadow-xl z-50 whitespace-nowrap
                                        ">
                                            {item.label}
                                        </div>
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}

                    <button
                        onClick={handleLogout}
                        className={`
                            flex items-center w-full group/item
                            rounded-xl transition-all duration-200
                            ${sidebarCollapsed ? 'justify-center py-3' : 'px-4 py-3 space-x-3'}
                            text-gray-600 hover:text-red-600 hover:bg-red-50
                        `}
                        title={sidebarCollapsed ? "Déconnexion" : ""}
                    >
                        <LogOut className="h-5 w-5" />

                        {!sidebarCollapsed ? (
                            <span className="text-sm font-medium">
                                Déconnexion
                            </span>
                        ) : (
                            <div className="
                                hidden group-hover/item:block absolute left-full ml-2
                                bg-gray-900 text-white px-3 py-2 rounded-lg
                                shadow-xl z-50 whitespace-nowrap
                            ">
                                Déconnexion
                            </div>
                        )}
                    </button>
                </nav>

                {/* User Profile - Toujours visible mais réduit */}
                <div className="mt-6">
                    <div className={`
                        flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'}
                        p-3 rounded-xl bg-gray-50
                        group border border-gray-100
                    `}>
                        <div className="relative">
                            <div className="
                                w-8 h-8 rounded-lg bg-gradient-to-br
                                from-blue-500 to-blue-600 flex items-center
                                justify-center shadow-sm
                            ">
                                <span className="text-xs font-bold text-white">JD</span>
                            </div>
                            <div className="
                                absolute -bottom-0.5 -right-0.5 w-3 h-3
                                bg-green-500 rounded-full border-2 border-white
                            " />
                        </div>

                        {!sidebarCollapsed ? (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    John Doe
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                    Admin
                                </p>
                            </div>
                        ) : (
                            // Tooltip au hover pour version réduite
                            <div className="
                                hidden group-hover:block absolute left-full ml-2
                                bg-gray-900 text-white px-3 py-2 rounded-lg
                                shadow-xl z-50 whitespace-nowrap
                            ">
                                <p className="font-semibold">John Doe</p>
                                <p className="text-xs text-gray-300">Admin</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;